import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Loader2, Download, CheckCircle2, XCircle, FileText, Archive, AlertTriangle, Save, Edit3, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Calendar, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import SepaSplitPanel from './sepa/SepaSplitPanel';
import { validateSepaIban } from '../../utils/sepa';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NATURA_OPTIONS = [
  { value: 'fornitore', label: 'Fornitore', color: 'bg-amber-100 text-amber-800' },
  { value: 'partner', label: 'Partner', color: 'bg-purple-100 text-purple-800' },
  { value: 'responsabile', label: 'Responsabile', color: 'bg-blue-100 text-blue-800' },
  { value: 'dipendente', label: 'Dipendente', color: 'bg-emerald-100 text-emerald-800' },
];

const MESI_IT = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const HW_ART15_PAYMENT_DESCRIPTION = 'Pagamento in nome e per conto di Holiday World — ex art. 15 DPR 633/72';

const fmtEur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return dt.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export default function InvoicePaymentTab() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('classify'); // 'classify' | 'select' | 'done'
  const [excluded, setExcluded] = useState(new Set());
  const [filterNatura, setFilterNatura] = useState('all');
  const [generating, setGenerating] = useState(false);
  const [classifications, setClassifications] = useState({});
  // IBAN editing state
  const [editingIban, setEditingIban] = useState(null); // invoice id being edited
  const [ibanDraft, setIbanDraft] = useState('');
  const [savingIban, setSavingIban] = useState(false);
  // Local IBAN overrides (saved during session)
  const [localIbans, setLocalIbans] = useState({});
  // Beneficiary name editing
  const [editingName, setEditingName] = useState(null);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [localXmlNames, setLocalXmlNames] = useState({});
  // Sorting state
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  // Sorting state for Step 1
  const [sortCol1, setSortCol1] = useState(null);
  const [sortDir1, setSortDir1] = useState('asc');
  // SEPA split config (Banca di Asti daily limit)
  const [splitConfig, setSplitConfig] = useState({ effective_limit_eur: 19500, daily_limit_eur: 20000 });
  // Data esecuzione bonifico (default: oggi). Permette di scegliere T+1 se l'upload è dopo il cut-off banca.
  const [executionDate, setExecutionDate] = useState(() => new Date().toISOString().slice(0, 10));
  // Bonifico Istantaneo (SCT Inst): aggiunge LclInstrm/INST nel XML. Default ON per coerenza con il toggle dello split.
  const [instantTransfers, setInstantTransfers] = useState(true);
  // Causali: override manuali per fattura
  const [customCausali, setCustomCausali] = useState({});
  const [editingCausale, setEditingCausale] = useState(null);
  const [causaleDraft, setCausaleDraft] = useState('');

  const toggleSort = (col) => {
    if (sortCol === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortCol(col); setSortDir('asc'); }
  };
  const toggleSort1 = (col) => {
    if (sortCol1 === col) { setSortDir1(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortCol1(col); setSortDir1('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ArrowUpDown className="h-3 w-3 ml-1 text-slate-300" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3 ml-1 text-slate-700" /> : <ArrowDown className="h-3 w-3 ml-1 text-slate-700" />;
  };
  const SortIcon1 = ({ col }) => {
    if (sortCol1 !== col) return <ArrowUpDown className="h-3 w-3 ml-1 text-slate-300" />;
    return sortDir1 === 'asc' ? <ArrowUp className="h-3 w-3 ml-1 text-slate-700" /> : <ArrowDown className="h-3 w-3 ml-1 text-slate-700" />;
  };

  const token = localStorage.getItem('hw_token');

  useEffect(() => {
    loadInvoices();
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/sepa-split/config`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setSplitConfig(await r.json());
      } catch (_) { /* ignore */ }
    })();
  }, []);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/invoices/payable`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const invs = data.invoices || [];
      setInvoices(invs);
      // Pre-fill ALL classifications from existing contact categories
      setClassifications(prev => {
        const cls = { ...prev };
        invs.forEach(inv => {
          if (!cls[inv.id]) {
            if (inv.contact_category && inv.contact_category !== 'da_classificare') {
              cls[inv.id] = inv.contact_category;
            }
          }
        });
        return cls;
      });
    } catch (err) {
      toast.error('Errore caricamento fatture');
    }
    setLoading(false);
  };

  const unclassified = invoices.filter(inv => !classifications[inv.id]);
  const allClassified = unclassified.length === 0 && invoices.length > 0;

  const handleClassify = (invoiceId, natura) => {
    setClassifications(prev => ({ ...prev, [invoiceId]: natura }));
  };

  const handleSaveClassifications = async (andContinue = false) => {
    const toSave = Object.keys(classifications).length;
    if (toSave === 0) {
      toast.error('Nessuna classificazione da salvare');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/invoices/classify-contacts`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ classifications })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`${data.classified} contatti classificati nella rubrica`);
        if (andContinue) {
          setStep('select');
        }
      }
    } catch (err) {
      toast.error('Errore salvataggio classificazioni');
    }
  };

  const toggleExclude = (id) => {
    setExcluded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Get effective IBAN (local override or from server)
  const getIban = (inv) => {
    if (localIbans[inv.supplier]) return localIbans[inv.supplier];
    return inv.supplier_iban || '';
  };

  // Check if IBAN is valid
  const isIbanValid = (inv) => {
    const iban = getIban(inv);
    if (!iban) return true; // no iban = not invalid, just missing
    if (localIbans[inv.supplier]) return validateSepaIban(iban).eligible;
    return inv.iban_valid !== false;
  };

  // Predicate riusato per Natura filter (tabella + split + totali + bottone)
  const matchesNaturaFilter = (inv) => {
    if (filterNatura === 'all') return true;
    const nat = classifications[inv.id] || inv.contact_category || 'da_classificare';
    return nat === filterNatura;
  };

  // Subset visibile in base al filtro Natura — usato per TUTTI i derivati
  const visibleInvoices = invoices.filter(matchesNaturaFilter);
  const visibleSelected = visibleInvoices.filter(inv => !excluded.has(inv.id));
  const visibleTotalAmount = visibleSelected.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0);
  const visibleSelectedWithIban = visibleSelected.filter(inv => getIban(inv) && isIbanValid(inv));
  const visibleSelectedWithInvalidIban = visibleSelected.filter(inv => getIban(inv) && !isIbanValid(inv));
  const visibleSelectedWithoutIban = visibleSelected.filter(inv => !getIban(inv));
  const visibleIbanAmount = visibleSelectedWithIban.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0);

  // Sort invoices for display
  const getSortedInvoices = (list) => {
    if (!sortCol) return list;
    return [...list].sort((a, b) => {
      let va, vb;
      switch (sortCol) {
        case 'supplier': va = (a.supplier || '').toLowerCase(); vb = (b.supplier || '').toLowerCase(); break;
        case 'iban': va = (getIban(a) || '').toLowerCase(); vb = (getIban(b) || '').toLowerCase(); break;
        case 'invoice_number': va = (a.invoice_number || ''); vb = (b.invoice_number || ''); break;
        case 'invoice_date': va = a.invoice_date || ''; vb = b.invoice_date || ''; break;
        case 'amount': return sortDir === 'asc' ? (parseFloat(a.amount) || 0) - (parseFloat(b.amount) || 0) : (parseFloat(b.amount) || 0) - (parseFloat(a.amount) || 0);
        case 'natura': {
          const ca = classifications[a.id] || a.contact_category || '';
          const cb = classifications[b.id] || b.contact_category || '';
          va = ca; vb = cb; break;
        }
        default: return 0;
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Sort for Step 1
  const getSortedInvoices1 = (list) => {
    if (!sortCol1) return list;
    return [...list].sort((a, b) => {
      let va, vb;
      switch (sortCol1) {
        case 'supplier': va = (a.supplier || '').toLowerCase(); vb = (b.supplier || '').toLowerCase(); break;
        case 'iban': va = (a.supplier_iban || '').toLowerCase(); vb = (b.supplier_iban || '').toLowerCase(); break;
        case 'invoice_number': va = (a.invoice_number || ''); vb = (b.invoice_number || ''); break;
        case 'amount': return sortDir1 === 'asc' ? (parseFloat(a.amount) || 0) - (parseFloat(b.amount) || 0) : (parseFloat(b.amount) || 0) - (parseFloat(a.amount) || 0);
        case 'due_date': va = a.due_date || ''; vb = b.due_date || ''; break;
        default: return 0;
      }
      if (va < vb) return sortDir1 === 'asc' ? -1 : 1;
      if (va > vb) return sortDir1 === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Save IBAN to backend + local state
  const handleSaveIban = async (inv) => {
    const iban = ibanDraft.trim().toUpperCase().replace(/\s/g, '');
    if (!iban) {
      toast.error('Inserisci un IBAN valido');
      return;
    }
    setSavingIban(true);
    try {
      const res = await fetch(`${API_URL}/api/invoices/update-supplier-iban`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ supplier_name: inv.supplier, iban })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`IBAN ${data.action === 'created' ? 'creato' : 'aggiornato'} per ${data.contact_name}`);
        setLocalIbans(prev => ({ ...prev, [inv.supplier]: iban }));
        setEditingIban(null);
        setIbanDraft('');
      } else {
        toast.error(data.detail || 'Errore salvataggio IBAN');
      }
    } catch (err) {
      toast.error('Errore salvataggio IBAN');
    }
    setSavingIban(false);
  };

  // Delete IBAN from backend + local state
  const handleDeleteIban = async (inv) => {
    if (!window.confirm(`Rimuovere l'IBAN per "${inv.supplier}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/invoices/delete-supplier-iban`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ supplier_name: inv.supplier })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`IBAN rimosso per ${data.contact_name}`);
        // Clear local override and force empty
        setLocalIbans(prev => {
          const next = { ...prev };
          next[inv.supplier] = '';
          return next;
        });
      } else {
        toast.error(data.detail || 'Errore');
      }
    } catch (err) {
      toast.error('Errore rimozione IBAN');
    }
  };

  // Get effective XML name for beneficiary
  const getXmlName = (inv) => {
    if (localXmlNames[inv.supplier]) return localXmlNames[inv.supplier];
    return inv.supplier_xml_name || '';
  };

  // Save XML name to backend + local state
  const handleSaveXmlName = async (inv) => {
    const xmlName = nameDraft.trim();
    if (!xmlName) { toast.error('Inserisci un nome'); return; }
    setSavingName(true);
    try {
      const res = await fetch(`${API_URL}/api/invoices/update-supplier-xml-name`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ supplier_name: inv.supplier, xml_name: xmlName })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Nome XML salvato: "${xmlName}" per ${data.contact_name}`);
        setLocalXmlNames(prev => ({ ...prev, [inv.supplier]: xmlName }));
        setEditingName(null);
        setNameDraft('');
      } else {
        toast.error(data.detail || 'Errore');
      }
    } catch { toast.error('Errore salvataggio nome'); }
    setSavingName(false);
  };


  const handleGenerateXml = async () => {
    // Warn about missing IBANs (sul subset filtrato)
    if (visibleSelectedWithoutIban.length > 0) {
      const proceed = window.confirm(
        `Attenzione: ${visibleSelectedWithoutIban.length} fatture selezionate non hanno IBAN.\n\n` +
        `Il file XML conterrà solo ${visibleSelectedWithIban.length} pagamenti con IBAN (${fmtEur(visibleIbanAmount)}).\n\n` +
        `Le fatture senza IBAN verranno comunque salvate nel batch ma non incluse nel file XML.\n\n` +
        `Vuoi continuare?`
      );
      if (!proceed) return;
    }

    if (visibleSelectedWithIban.length === 0) {
      toast.error('Nessuna fattura selezionata ha un IBAN. Inserisci almeno un IBAN per procedere.');
      return;
    }

    setGenerating(true);
    try {
      const invoiceIds = visibleSelected.map(inv => inv.id);
      const doRequest = (force) => fetch(`${API_URL}/api/invoices/generate-payment-xml`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_ids: invoiceIds, classifications, custom_causali: customCausali, execution_date: executionDate, instant_transfers: instantTransfers, force })
      });
      let res = await doRequest(false);
      if (res.status === 409) {
        const err = await res.json().catch(() => ({}));
        const warns = err.detail?.duplicate_warnings || [];
        const proceed = window.confirm(
          `⚠️ CONTROLLO DOPPIONI — possibili pagamenti duplicati:\n\n${warns.map(w => '• ' + w).join('\n\n')}\n\nVuoi generare comunque l'XML?`
        );
        if (!proceed) { setGenerating(false); return; }
        res = await doRequest(true);
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : `Errore ${res.status}`);
      }
      const blob = await res.blob();
      // Beneficiari esclusi: coordinate non valide o bonifico extra-SEPA
      const excludedCount = parseInt(res.headers.get('X-Excluded-Count') || '0', 10);
      if (excludedCount > 0) {
        try {
          const info = JSON.parse(atob(res.headers.get('X-Excluded-Info') || ''));
          const lines = info.map(r => `• ${r.supplier} (fatt. ${r.invoice_number || '—'}): ${r.iban || 'IBAN mancante'} — ${r.reason}`).join('\n');
          window.alert(`⚠️ ATTENZIONE: ${excludedCount} fatture ESCLUSE dall’XML SEPA:\n\n${lines}\n\nCorreggi le coordinate non valide; i bonifici extra-SEPA devono essere gestiti separatamente.`);
        } catch (_) {
          window.alert(`⚠️ ATTENZIONE: ${excludedCount} fatture escluse dall’XML SEPA. Controlla la Rubrica Beneficiari e gestisci separatamente gli extra-SEPA.`);
        }
      }
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="(.+?)"/);
      const filename = match ? match[1] : 'pagamento_fatture.xml';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`XML generato: ${filename}`);
      setStep('done');
    } catch (err) {
      toast.error(err.message || 'Errore generazione XML');
    }
    setGenerating(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        <span className="ml-3 text-slate-500">Caricamento fatture da pagare...</span>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
          <p className="text-lg font-medium text-slate-700">Nessuna fattura da pagare</p>
          <p className="text-sm text-slate-500 mt-1">Tutte le fatture sono state pagate o non ci sono fatture in sospeso/scadute.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="invoice-payment-tab">
      {/* Steps indicator */}
      <div className="flex gap-4 mb-4">
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${step === 'classify' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500'}`}>
          1. Classifica Beneficiari
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${step === 'select' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500'}`}>
          2. Seleziona e Inserisci IBAN
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${step === 'done' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
          3. Genera XML e Salva in Archivio
        </div>
      </div>

      {/* Step 1: Classify */}
      {step === 'classify' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Classifica i beneficiari</h3>
              <p className="text-sm text-slate-500">
                {unclassified.length > 0
                  ? `${Object.keys(classifications).length} di ${invoices.length} classificati (${unclassified.length} rimanenti)`
                  : `Tutti i ${invoices.length} beneficiari sono classificati`}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="save-classifications-btn"
                variant="outline"
                onClick={() => handleSaveClassifications(false)}
                disabled={Object.keys(classifications).length === 0}
              >
                <Save className="h-4 w-4 mr-1.5" />
                Salva{Object.keys(classifications).length > 0 ? ` (${Object.keys(classifications).length})` : ''}
              </Button>
              <Button
                data-testid="proceed-to-select-btn"
                onClick={() => handleSaveClassifications(true)}
                disabled={Object.keys(classifications).length === 0}
                className="bg-slate-800 hover:bg-slate-900 text-white"
              >
                {allClassified ? 'Salva e Vai ai Pagamenti' : 'Salva e Continua'}
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <table className="w-full" data-testid="classify-table">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort1('supplier')}>
                    <span className="inline-flex items-center">Beneficiario<SortIcon1 col="supplier" /></span>
                  </th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort1('iban')}>
                    <span className="inline-flex items-center">IBAN<SortIcon1 col="iban" /></span>
                  </th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort1('invoice_number')}>
                    <span className="inline-flex items-center">Fattura<SortIcon1 col="invoice_number" /></span>
                  </th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort1('amount')}>
                    <span className="inline-flex items-center justify-end">Importo<SortIcon1 col="amount" /></span>
                  </th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort1('due_date')}>
                    <span className="inline-flex items-center">Scadenza<SortIcon1 col="due_date" /></span>
                  </th>
                  <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Natura</th>
                </tr>
              </thead>
              <tbody>
                {getSortedInvoices1(invoices).map((inv, i) => {
                  return (
                  <tr key={inv.id} className={`border-b transition-colors ${classifications[inv.id] ? 'bg-slate-50/50' : 'bg-amber-50/30'}`}>
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-sm text-slate-800">{inv.supplier}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="font-mono text-xs text-slate-500">{inv.supplier_iban || '-'}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-sm text-slate-600">{inv.invoice_number || '-'}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="font-semibold text-sm">{fmtEur(inv.amount)}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`text-sm ${inv.is_overdue ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                        {fmtDate(inv.due_date)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <div className="flex gap-1 justify-center">
                        {NATURA_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            data-testid={`natura-${opt.value}-${i}`}
                            onClick={() => handleClassify(inv.id, opt.value)}
                            className={`px-2 py-0.5 rounded-full text-xs font-medium transition-all ${
                              classifications[inv.id] === opt.value
                                ? opt.color + ' ring-2 ring-offset-1 ring-slate-400'
                                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Step 2: Select payments + IBAN editing */}
      {step === 'select' && (
        <div>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-white rounded-lg border p-3 text-center">
              <p className="text-xs text-slate-500">Fatture totali</p>
              <p className="text-xl font-bold text-slate-800">{invoices.length}</p>
              {filterNatura !== 'all' && (
                <p className="text-[10px] text-slate-500 mt-0.5">Filtrate: {visibleInvoices.length}</p>
              )}
            </div>
            <div className="bg-white rounded-lg border p-3 text-center">
              <p className="text-xs text-slate-500">Selezionate{filterNatura !== 'all' ? ' (filtrate)' : ''}</p>
              <p className="text-xl font-bold text-emerald-600">{visibleSelected.length}</p>
            </div>
            <div className={`rounded-lg border p-3 text-center ${visibleSelectedWithoutIban.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
              <p className="text-xs text-slate-600">Con IBAN (nel XML)</p>
              <p className="text-xl font-bold text-emerald-700">{visibleSelectedWithIban.length}</p>
              {visibleSelectedWithoutIban.length > 0 && (
                <p className="text-[10px] text-amber-600 mt-0.5">{visibleSelectedWithoutIban.length} senza IBAN</p>
              )}
            </div>
            <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-3 text-center" data-testid="total-amount-box">
              <p className="text-xs text-emerald-700">Importo XML{filterNatura !== 'all' ? ` (${filterNatura})` : ''}</p>
              <p className="text-xl font-bold text-emerald-700">{fmtEur(visibleIbanAmount)}</p>
              {visibleIbanAmount !== visibleTotalAmount && (
                <p className="text-[10px] text-slate-500">Tot. selez.: {fmtEur(visibleTotalAmount)}</p>
              )}
            </div>
          </div>

          {/* Warning banner for missing/invalid IBANs */}
          {(visibleSelectedWithoutIban.length > 0 || visibleSelectedWithInvalidIban.length > 0) && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 space-y-1" data-testid="iban-warning">
              {visibleSelectedWithoutIban.length > 0 && (
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <span className="text-sm"><span className="font-medium text-amber-800">{visibleSelectedWithoutIban.length} senza IBAN.</span><span className="text-amber-700 ml-1">Clicca "+ Inserisci IBAN" per aggiungerlo.</span></span>
                </div>
              )}
              {visibleSelectedWithInvalidIban.length > 0 && (
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                  <span className="text-sm"><span className="font-medium text-red-800">{visibleSelectedWithInvalidIban.length} non inseribili nell’XML SEPA</span><span className="text-red-700 ml-1">Checksum, Paese extra-SEPA o coordinate non valide: correggi o gestisci separatamente.</span></span>
                </div>
              )}
            </div>
          )}

          {/* SEPA Split Panel - shown only when total exceeds threshold */}
          {visibleIbanAmount > splitConfig.effective_limit_eur && visibleSelectedWithIban.length > 0 && (
            <div className="mb-4">
              <SepaSplitPanel
                jsonBody={{
                  invoice_ids: visibleSelected.filter(inv => getIban(inv) && isIbanValid(inv)).map(inv => inv.id),
                  classifications,
                  custom_causali: customCausali,
                  execution_date: executionDate,
                }}
                planEndpoint="/api/invoices/sepa-split-plan"
                downloadEndpoint="/api/invoices/sepa-split-download"
                filenamePrefix="pagamento_fatture"
                title={`Split SEPA — Limite Banca di Asti (Pagamento Fatture${filterNatura !== 'all' ? ' — ' + filterNatura : ''})`}
                onDownloaded={({ split_group_id }) => {
                  toast.success(`ZIP scaricato. Gruppo split: ${split_group_id}`);
                  setStep('done');
                }}
              />
            </div>
          )}

          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Seleziona pagamenti e inserisci IBAN</h3>
              <p className="text-sm text-slate-500">
                {visibleSelectedWithIban.length} pagamenti nel XML | Totale: <span className="font-semibold">{fmtEur(visibleIbanAmount)}</span>
                {filterNatura !== 'all' && <span className="ml-2 text-purple-600 font-medium">(filtro: {filterNatura})</span>}
              </p>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              {/* Bonifico Istantaneo (SCT Inst) */}
              <label className="inline-flex items-center gap-1.5 text-sm px-2 py-1 border rounded-md bg-white cursor-pointer" title="Aggiunge il tag SCT Inst nel XML. Necessario se sul portale Banca di Asti carichi in modalità 'Bonifico istantaneo'.">
                <input
                  type="checkbox"
                  checked={instantTransfers}
                  onChange={(e) => setInstantTransfers(e.target.checked)}
                  data-testid="instant-transfers-toggle"
                />
                <Zap className={`h-4 w-4 ${instantTransfers ? 'text-blue-600' : 'text-slate-400'}`} />
                <span className="text-slate-700">Istantaneo</span>
              </label>
              {/* Data esecuzione bonifico */}
              <div className="flex items-center gap-1.5 text-sm" data-testid="exec-date-wrapper">
                <Calendar className="h-4 w-4 text-slate-500" />
                <label className="text-slate-600">Data:</label>
                <input
                  type="date"
                  value={executionDate}
                  onChange={(e) => setExecutionDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="px-2 py-1 border rounded-md text-sm bg-white"
                  data-testid="exec-date-input"
                  title="Data di esecuzione del bonifico"
                />
              </div>
              {/* Filtro Natura */}
              <select
                value={filterNatura}
                onChange={(e) => setFilterNatura(e.target.value)}
                className="px-3 py-1.5 border rounded-md text-sm bg-white"
                data-testid="filter-natura"
              >
                <option value="all">Tutte le classificazioni</option>
                <option value="da_classificare">Da classificare</option>
                {NATURA_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <Button variant="outline" onClick={() => setStep('classify')} data-testid="back-to-classify-btn">
                Indietro
              </Button>
              <Button
                data-testid="generate-xml-btn"
                onClick={handleGenerateXml}
                disabled={generating || visibleSelectedWithIban.length === 0 || visibleIbanAmount > splitConfig.effective_limit_eur}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                title={visibleIbanAmount > splitConfig.effective_limit_eur ? 'Disabilitato: totale supera la soglia, usa lo split sopra' : ''}
              >
                {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                {generating ? 'Generazione...' : `Genera XML e Salva (${visibleSelectedWithIban.length} pagamenti)`}
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <table className="w-full" data-testid="select-table">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="w-10 px-3 py-2">
                    {(() => {
                      const filteredIds = visibleInvoices.map(i => i.id);
                      const allFilteredSelected = filteredIds.length > 0 && filteredIds.every(id => !excluded.has(id));
                      return (
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          title={filterNatura === 'all' ? 'Seleziona/Deseleziona tutti' : `Seleziona/Deseleziona tutti i filtrati (${filteredIds.length})`}
                          onChange={() => {
                            const newSet = new Set(excluded);
                            if (allFilteredSelected) {
                              filteredIds.forEach(id => newSet.add(id));
                            } else {
                              filteredIds.forEach(id => newSet.delete(id));
                            }
                            setExcluded(newSet);
                          }}
                          className="rounded"
                          data-testid="select-all-header"
                        />
                      );
                    })()}
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort('supplier')}>
                    <span className="inline-flex items-center">Beneficiario<SortIcon col="supplier" /></span>
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase min-w-[200px] cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort('iban')}>
                    <span className="inline-flex items-center">IBAN<SortIcon col="iban" /></span>
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort('invoice_number')}>
                    <span className="inline-flex items-center">N. Fattura<SortIcon col="invoice_number" /></span>
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort('invoice_date')}>
                    <span className="inline-flex items-center">Data<SortIcon col="invoice_date" /></span>
                  </th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort('amount')}>
                    <span className="inline-flex items-center justify-end">Importo<SortIcon col="amount" /></span>
                  </th>
                  <th className="text-center px-3 py-2 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort('natura')}>
                    <span className="inline-flex items-center">Natura<SortIcon col="natura" /></span>
                  </th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase">Causale</th>
                </tr>
              </thead>
              <tbody>
                {getSortedInvoices(visibleInvoices).map((inv, i) => {
                  const isExcluded = excluded.has(inv.id);
                  const natura = classifications[inv.id] || inv.contact_category || 'da_classificare';
                  const naturaOpt = NATURA_OPTIONS.find(o => o.value === natura);
                  const isHwArt15 = natura === 'partner' || natura === 'responsabile';
                  const autoCausale = isHwArt15
                    ? HW_ART15_PAYMENT_DESCRIPTION
                    : natura === 'dipendente'
                      ? (() => { const d = executionDate ? new Date(executionDate + 'T00:00:00') : new Date(); return `Pagamento busta paga ${MESI_IT[d.getMonth()]} ${d.getFullYear()}`; })()
                      : `pagamento Fattura/ric n ${inv.invoice_number || '...'} del ${fmtDate(inv.invoice_date)}`;
                  const causale = isHwArt15 ? HW_ART15_PAYMENT_DESCRIPTION : (customCausali[inv.id] || autoCausale);
                  const effectiveIban = getIban(inv);
                  const isEditingThis = editingIban === inv.id;
                  
                  return (
                    <tr key={inv.id} className={`border-b transition-colors ${isExcluded ? 'bg-red-50/30 opacity-50' : !effectiveIban && !isExcluded ? 'bg-amber-50/20' : 'hover:bg-slate-50'}`}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          data-testid={`select-invoice-${i}`}
                          checked={!isExcluded}
                          onChange={() => toggleExclude(inv.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-3 py-2">
                        {editingName === inv.id ? (
                          <div className="flex items-center gap-1">
                            <input type="text" value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
                              placeholder="Nome per XML..." className="w-full px-2 py-1 border rounded text-xs focus:ring-1 focus:ring-blue-500" autoFocus
                              data-testid={`xml-name-input-${i}`}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveXmlName(inv); if (e.key === 'Escape') { setEditingName(null); setNameDraft(''); } }} />
                            <button onClick={() => handleSaveXmlName(inv)} disabled={savingName} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded">
                              {savingName ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                            </button>
                            <button onClick={() => { setEditingName(null); setNameDraft(''); }} className="p-1 text-slate-400 hover:bg-slate-100 rounded"><XCircle className="h-3.5 w-3.5" /></button>
                          </div>
                        ) : (
                          <div className="group">
                            <div className="flex items-center gap-1">
                              <span className="font-medium text-sm">{inv.supplier}</span>
                              <button onClick={() => { setEditingName(inv.id); setNameDraft(getXmlName(inv) || inv.supplier); }}
                                className="p-0.5 text-slate-300 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Modifica nome per XML">
                                <Edit3 className="h-3 w-3" />
                              </button>
                            </div>
                            {getXmlName(inv) && getXmlName(inv) !== inv.supplier && (
                              <div className="text-[10px] text-blue-600 mt-0.5">XML: {getXmlName(inv)}</div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {isEditingThis ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={ibanDraft}
                              onChange={(e) => setIbanDraft(e.target.value.toUpperCase())}
                              placeholder="IT..."
                              className="w-full px-2 py-1 border rounded text-xs font-mono focus:ring-1 focus:ring-blue-500"
                              autoFocus
                              data-testid={`iban-input-${i}`}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveIban(inv);
                                if (e.key === 'Escape') { setEditingIban(null); setIbanDraft(''); }
                              }}
                            />
                            <button
                              onClick={() => handleSaveIban(inv)}
                              disabled={savingIban}
                              className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                              data-testid={`save-iban-${i}`}
                            >
                              {savingIban ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                            </button>
                            <button
                              onClick={() => { setEditingIban(null); setIbanDraft(''); }}
                              className="p-1 text-slate-400 hover:bg-slate-100 rounded"
                            >
                              <XCircle className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : effectiveIban ? (
                          <div className="flex items-center gap-1 group">
                            <span className={`font-mono text-xs ${isIbanValid(inv) ? 'text-slate-600' : 'text-red-600 font-bold'}`}>
                              {effectiveIban}
                              {!isIbanValid(inv) && <span className="ml-1 text-[10px] text-red-500 font-normal">({effectiveIban.length} car.)</span>}
                            </span>
                            <button
                              onClick={() => { setEditingIban(inv.id); setIbanDraft(effectiveIban); }}
                              className="p-0.5 text-slate-300 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Modifica IBAN"
                            >
                              <Edit3 className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => handleDeleteIban(inv)}
                              className="p-0.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Rimuovi IBAN"
                              data-testid={`delete-iban-${i}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setEditingIban(inv.id); setIbanDraft(''); }}
                            className="text-xs text-amber-600 hover:text-amber-800 border border-dashed border-amber-300 px-2 py-0.5 rounded hover:bg-amber-50 transition-colors"
                            data-testid={`add-iban-${i}`}
                          >
                            + Inserisci IBAN
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm">{inv.invoice_number || '-'}</td>
                      <td className="px-3 py-2 text-sm text-slate-600">{fmtDate(inv.invoice_date)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-sm">{fmtEur(inv.amount)}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${naturaOpt?.color || 'bg-gray-100 text-gray-600'}`}>
                          {naturaOpt?.label || natura}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 max-w-[250px]">
                        {editingCausale === inv.id ? (
                          <div className="flex items-center gap-1">
                            <input type="text" value={causaleDraft} onChange={(e) => setCausaleDraft(e.target.value)}
                              maxLength={140}
                              className="w-full px-2 py-1 border rounded text-xs focus:ring-1 focus:ring-blue-500" autoFocus
                              data-testid={`causale-input-${i}`}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && causaleDraft.trim()) { setCustomCausali(prev => ({ ...prev, [inv.id]: causaleDraft.trim() })); setEditingCausale(null); }
                                if (e.key === 'Escape') { setEditingCausale(null); setCausaleDraft(''); }
                              }} />
                            <button onClick={() => { if (causaleDraft.trim()) { setCustomCausali(prev => ({ ...prev, [inv.id]: causaleDraft.trim() })); } setEditingCausale(null); }}
                              className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" data-testid={`save-causale-${i}`}>
                              <Save className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => { setEditingCausale(null); setCausaleDraft(''); }} className="p-1 text-slate-400 hover:bg-slate-100 rounded">
                              <XCircle className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 group">
                            <span data-testid={`payment-causale-${i}`} className={`truncate ${customCausali[inv.id] && !isHwArt15 ? 'text-blue-700 font-medium' : ''}`} title={customCausali[inv.id] && !isHwArt15 ? `Causale personalizzata: ${causale}` : causale}>
                              {causale}
                            </span>
                            {!isHwArt15 && <button onClick={() => { setEditingCausale(inv.id); setCausaleDraft(causale); }}
                              className="p-0.5 text-slate-300 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" title="Modifica causale"
                              data-testid={`edit-causale-${i}`}>
                              <Edit3 className="h-3 w-3" />
                            </button>}
                            {customCausali[inv.id] && !isHwArt15 && (
                              <button onClick={() => setCustomCausali(prev => { const n = { ...prev }; delete n[inv.id]; return n; })}
                                className="p-0.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" title="Ripristina causale automatica"
                                data-testid={`reset-causale-${i}`}>
                                <XCircle className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Step 3: Done - Saved to archive */}
      {step === 'done' && (
        <Card>
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-700">XML generato e salvato nell'Archivio Pagamenti Fatture!</p>
            <p className="text-sm text-slate-500 mt-1">
              {visibleSelectedWithIban.length} pagamenti per un totale di {fmtEur(visibleIbanAmount)}
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4 max-w-md mx-auto text-left">
              <p className="text-sm font-medium text-blue-800">Prossimo passo:</p>
              <p className="text-sm text-blue-700 mt-1">
                Vai nell'<strong>Archivio Pagamenti Fatture</strong> e carica il PDF della risposta banca.
                Una volta caricato, le fatture verranno automaticamente segnate come <strong>Pagate</strong>.
              </p>
            </div>
            <div className="flex gap-3 justify-center mt-5">
              <Button variant="outline" onClick={() => { setStep('classify'); setExcluded(new Set()); setLocalIbans({}); loadInvoices(); }} data-testid="new-payment-btn">
                Nuovo Pagamento
              </Button>
              <Button onClick={() => navigate('/payment-archive')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="go-to-archive-btn">
                <Archive className="h-4 w-4 mr-2" />Vai all'Archivio Pagamenti Fatture
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
